/*
 * FilthyBass — buy from the bundle stage (sections/product-bundle.liquid, tier action 'stage').
 *
 * Owner, 2026-10-21: "whenever you hover over a garment and it jumps out, you get a size picker and
 * add to cart button, without having to click through". So, for every garment on a stage:
 *   - a mouse resting on it (~120 ms) lifts it out and opens the tier's quick-buy panel: colour
 *     swatches, sizes, Add. The panel stays while the pointer is on the garment or the panel; a
 *     click pins it. A tap or a key opens it pinned. Esc, x or a click elsewhere closes it.
 *   - a swatch turns the garment over like a record sleeve (two 225 ms halves) and it lands in the
 *     new colour. The size carries over when the new colour has it.
 *   - clicking a size puts that garment in the cart (owner, 2026-10-22: "added when size clicked"); a
 *     one-size product (a tote) has an Add button. Either way the cart drawer opens (owner, 2026-10-22)
 *     and the tier names the next saving.
 *   - a garment wears "Added" while it is in the cart: the stickers follow the cart itself, so a
 *     remove in the drawer takes the sticker off.
 *   - the tier's button adds exactly its stage, in the colours and sizes picked; a garment still
 *     without a size opens its picker first. Then the cart drawer opens.
 * Tier prices follow every pick: the discount is taken per line and floored, as Shopify does.
 */
(() => {
  const OPEN_DELAY = 120;
  const CLOSE_GRACE = 250;
  const FLIP_HALF = 225;

  // Liquid hands the colourway label over already HTML-escaped ("earth &amp; gold print"): decode it
  // once, so esc() below escapes it once.
  const decode = (s) => new DOMParser().parseFromString(`<!doctype html><body>${s}`, 'text/html').body.textContent;

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // placeholders are [name]: a } inside a Liquid {{ }} ends the tag, so the section cannot write {name}
  const fill = (template, values) => template.replace(/\[(\w+)\]/g, (m, k) => (k in values ? values[k] : m));

  // The shop's own money format ("€{{amount_with_comma_separator}}"), so a price the script writes
  // reads exactly like the ones Liquid wrote.
  function money(cents, format) {
    const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const parts = (decimals, thousands, point) => {
      const [whole, frac] = (cents / 100).toFixed(decimals).split('.');
      return group(whole, thousands) + (frac ? point + frac : '');
    };
    return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, kind) => {
      switch (kind) {
        case 'amount_no_decimals':
          return parts(0, ',', '.');
        case 'amount_with_comma_separator':
          return parts(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return parts(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return parts(2, "'", '.');
        default:
          return parts(2, ',', '.');
      }
    });
  }

  function init(root) {
    if (root.dataset.fbQuickReady) return;
    const holder = root.querySelector('[data-fb-stage-data]');
    if (!holder) return;
    root.dataset.fbQuickReady = '1';
    const data = JSON.parse(holder.textContent);
    Object.values(data.designs).forEach((versions) =>
      versions.forEach((v) => {
        v.name = decode(v.name);
        v.label = decode(v.label);
      })
    );
    const S = data.strings;
    const tiers = [...(data.tiers || [])].sort((a, b) => a.qty - b.qty);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let current = null; // the garment whose panel is open
    let pinned = false;
    let openTimer = 0;
    let closeTimer = 0;
    // A tier whose "Add all" stopped at a garment without a size. Its panel covers the tier's button,
    // so picking that size carries the add on: the next garment without one, or the whole stage.
    let pending = null;

    // ---- a garment's state ----
    const versionsOf = (g) => data.designs[g.dataset.design] || [];
    const versionOf = (g) => versionsOf(g).find((v) => String(v.id) === g.dataset.version);
    const needsSize = (v) => v && v.variants.length > 1;

    function chosen(g) {
      const v = versionOf(g);
      if (!v) return null;
      if (!needsSize(v)) return v.variants[0].available ? v.variants[0] : null;
      if (!g.dataset.variant && v.id === data.thisProduct) {
        // the size already picked in the buy box above counts
        const fromUrl = new URLSearchParams(window.location.search).get('variant');
        const match = v.variants.find((x) => String(x.id) === fromUrl && x.available);
        if (match) g.dataset.variant = match.id;
      }
      return v.variants.find((x) => String(x.id) === g.dataset.variant) || null;
    }

    function priceOf(g) {
      const pick = chosen(g);
      if (pick) return pick.price;
      const v = versionOf(g);
      const first = v && (v.variants.find((x) => x.available) || v.variants[0]);
      return first ? first.price : 0;
    }

    // ---- tier prices and button ----
    function reprice(tier) {
      const garments = [...tier.querySelectorAll('[data-fb-stage-garment]')];
      if (!garments.length) return;
      const off = parseInt(tier.dataset.off, 10) || 0;
      let gross = 0;
      let net = 0;
      garments.forEach((g) => {
        const p = priceOf(g);
        gross += p;
        net += p - Math.floor((p * off) / 100);
      });
      const now = tier.querySelector('[data-fb-now]');
      const was = tier.querySelector('[data-fb-was]');
      const each = tier.querySelector('[data-fb-each]');
      if (now) now.textContent = money(net, data.money);
      if (was) was.textContent = money(gross, data.money);
      if (each) each.textContent = `${money(Math.floor(net / garments.length), data.money)} ${S.each}`;
      const button = tier.querySelector('[data-fb-stage-add]');
      if (button && !button.hasAttribute('aria-busy')) {
        const n = garments.length;
        const template = n === 1 ? S.addOne : n === 2 ? S.addBoth : S.addAll;
        button.textContent = fill(template, { count: n, price: money(net, data.money) });
      }
    }

    // ---- the panel ----
    function panelOf(g) {
      return g.closest('.fb-bundle__tier').querySelector('[data-fb-quick]');
    }

    function render(g) {
      const panel = panelOf(g);
      const v = versionOf(g);
      if (!panel || !v) return;
      const versions = versionsOf(g);
      const pick = chosen(g);
      const price = pick ? pick.price : priceOf(g);
      const swatches =
        versions.length > 1
          ? `<div class="fb-bundle__quick-row" role="group" aria-label="${esc(S.colour)}">${versions
              .map(
                (x) =>
                  `<button type="button" class="fb-sw" style="${esc(x.sw)}" data-fb-v="${x.id}" aria-label="${esc(
                    x.label
                  )}" aria-pressed="${x.id === v.id}"></button>`
              )
              .join('')}</div>`
          : '';
      const sizes = needsSize(v)
        ? `<div class="fb-bundle__quick-row fb-bundle__quick-sizes" role="group" aria-label="${esc(S.size)}">${v.variants
            .map(
              (x) =>
                `<button type="button" class="fb-bundle__size" data-fb-size="${x.id}" aria-pressed="${
                  pick && pick.id === x.id
                }"${x.available ? '' : ' disabled'} aria-label="${esc(x.size || '')}${
                  x.available ? '' : ` — ${esc(S.soldOut)}`
                }">${esc(x.size || '')}</button>`
            )
            .join('')}</div>`
        : `<p class="fb-bundle__quick-one">${esc(S.oneSize)}</p>`;
      const sold = !v.variants.some((x) => x.available);
      const price$ = money(price, data.money);
      // A sized garment is added by clicking its size; a one-size one (a tote) by its Add button.
      const action = sold
        ? `<p class="fb-bundle__quick-hint">${esc(S.soldOut)}</p>`
        : needsSize(v)
        ? ''
        : `<button type="button" class="button button--full-width fb-bundle__quick-add" data-fb-quick-add>${esc(
            fill(S.add, { price: price$ })
          )}</button>`;
      const hint = !sold && needsSize(v) ? `<p class="fb-bundle__quick-hint">${esc(fill(S.pickToAdd, { price: price$ }))}</p>` : '';
      panel.setAttribute('aria-label', v.name);
      panel.innerHTML = `
        <button type="button" class="fb-bundle__quick-close" data-fb-close aria-label="${esc(S.close)}">&times;</button>
        <p class="fb-bundle__quick-name">${esc(v.name)}</p>
        <p class="fb-bundle__quick-label">${esc(v.label)}</p>
        ${swatches}
        ${hint}
        ${sizes}
        ${action}
        <p class="fb-bundle__quick-msg" role="status" data-fb-msg></p>`;
    }

    function open(g, pin, focus) {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (current && current !== g) close(false);
      current = g;
      pinned = pinned || pin;
      const tier = g.closest('.fb-bundle__tier');
      render(g);
      versionsOf(g).forEach((x) => {
        const img = new Image();
        img.src = x.img;
      });
      tier.classList.add('is-picking');
      g.classList.add('is-lifted');
      g.setAttribute('aria-expanded', 'true');
      panelOf(g).hidden = false;
      if (focus) {
        const first = panelOf(g).querySelector('.fb-sw[aria-pressed="true"], .fb-bundle__size:not([disabled]), [data-fb-quick-add]');
        if (first) first.focus();
      }
    }

    function close(refocus) {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (!current) return;
      const g = current;
      const panel = panelOf(g);
      if (panel) {
        panel.hidden = true;
        panel.innerHTML = '';
      }
      g.classList.remove('is-lifted');
      g.setAttribute('aria-expanded', 'false');
      g.closest('.fb-bundle__tier').classList.remove('is-picking');
      current = null;
      pinned = false;
      pending = null;
      if (refocus) g.focus();
    }

    const closeSoon = () => {
      if (pinned) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => close(false), CLOSE_GRACE);
    };

    // ---- the sleeve flip ----
    function swap(g, v) {
      const img = g.querySelector('img');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = v.img;
      g.classList.toggle('fb-bundle__garment--flat', !!v.flat);
    }

    async function switchTo(g, id) {
      const from = versionOf(g);
      const to = versionsOf(g).find((x) => String(x.id) === String(id));
      if (!to || !from || to.id === from.id || g.dataset.flipping) return;
      g.dataset.flipping = '1';
      // the size carries over when the new colour has it in stock
      const size = (chosen(g) || {}).size;
      const same = to.variants.find((x) => x.size === size && x.available);
      if (same) g.dataset.variant = same.id;
      else delete g.dataset.variant;
      g.dataset.version = to.id;
      g.classList.remove('is-added');
      g.querySelector('[data-fb-added]').hidden = true;
      g.setAttribute('aria-label', fill(needsSize(to) ? S.choose : S.chooseColour, { title: to.name }));
      render(g);
      reprice(g.closest('.fb-bundle__tier'));

      const img = g.querySelector('img');
      const ready = new Image();
      ready.src = to.img;
      const loaded = ready.decode ? ready.decode().catch(() => {}) : Promise.resolve();
      try {
        if (reduceMotion.matches || !img.animate) {
          await loaded;
          swap(g, to);
        } else {
          const turn = (a, b, easing) =>
            img.animate([{ transform: `perspective(60rem) rotateY(${a}deg)` }, { transform: `perspective(60rem) rotateY(${b}deg)` }], {
              duration: FLIP_HALF,
              easing,
            }).finished;
          await Promise.all([turn(0, 90, 'cubic-bezier(0.5, 0, 1, 1)'), loaded]);
          swap(g, to);
          await turn(-90, 0, 'cubic-bezier(0, 0, 0.3, 1)');
        }
      } finally {
        delete g.dataset.flipping;
      }
    }

    // ---- the cart ----
    // Every add opens the cart drawer (owner, 2026-10-22). Removing the last item marks <cart-drawer>
    // is-empty, which hides its items; Dawn's own product form clears that before re-rendering, and so
    // must this, or "Add all" opened a drawer with nothing in it (owner, 2026-10-22).
    function post(items) {
      const cart = document.querySelector('cart-drawer');
      const body = { items };
      body.sections = cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
      body.sections_url = window.location.pathname;
      return fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.status) throw new Error(response.description || response.message);
          if (cart && cart.renderContents) {
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }
          return refresh();
        });
    }

    // The stickers and the tier line follow the CART, not this script's memory: a garment wears "Added"
    // while its variant is in the cart - after a reload, after a remove in the drawer, after an add from
    // the drawer's rail (owner, 2026-10-22). A garment without a picked size adopts the size in the cart.
    let lastTier = null;
    function applyCart(cart) {
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      root.querySelectorAll('[data-fb-stage-garment]').forEach((g) => {
        const v = versionOf(g);
        if (!v) return;
        const pick = chosen(g);
        let added = !!pick && inCart.has(pick.id);
        if (!pick) {
          const hit = v.variants.find((x) => inCart.has(x.id));
          if (hit) {
            g.dataset.variant = hit.id;
            added = true;
          }
        }
        g.classList.toggle('is-added', added);
        g.querySelector('[data-fb-added]').hidden = !added;
      });
      root.querySelectorAll('.fb-bundle__tier').forEach(reprice);
      root.querySelectorAll('[data-fb-status]').forEach((s) => {
        s.textContent = '';
      });
      if (cart.item_count > 0 && lastTier) nudge(lastTier, cart.item_count);
    }

    function refresh() {
      return fetch(`${window.routes.cart_url}.js`)
        .then((r) => r.json())
        .then((cart) => {
          applyCart(cart);
          return cart;
        });
    }

    let refreshTimer = 0;
    const refreshSoon = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => refresh().catch(() => {}), 250);
    };

    function nudge(tier, count) {
      const status = tier.querySelector('[data-fb-status]');
      if (!status) return;
      let off = 0;
      tiers.forEach((t) => {
        if (count >= t.qty) off = Math.max(off, t.off);
      });
      const next = tiers.find((t) => t.qty > count && t.off > off);
      if (next) status.textContent = fill(S.inCartNext, { count, more: next.qty - count, percent: next.off });
      else if (off > 0) status.textContent = fill(S.inCartDone, { count, percent: off });
      else status.textContent = '';
    }

    function addOne(g, button) {
      const pick = chosen(g);
      const msg = panelOf(g).querySelector('[data-fb-msg]');
      if (!pick) {
        msg.textContent = S.pickSize;
        const row = panelOf(g).querySelector('.fb-bundle__quick-sizes');
        if (row) {
          row.classList.remove('is-nudged');
          void row.offsetWidth;
          row.classList.add('is-nudged');
          const first = row.querySelector('.fb-bundle__size:not([disabled])');
          if (first) first.focus();
        }
        return;
      }
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      lastTier = g.closest('.fb-bundle__tier');
      close(false);
      post([{ id: pick.id, quantity: 1 }]).catch((err) => {
        console.error('[fb-bundle-stage]', err);
        const status = lastTier && lastTier.querySelector('[data-fb-status]');
        if (status) status.textContent = (window.cartStrings && window.cartStrings.error) || String(err.message || err);
      });
    }

    // Adds the stage's garments that are not in the cart yet: one already added is not added twice.
    // It reads the cart first: the stickers follow the cart ~250 ms late (debounced), and an "Add all"
    // clicked right after a remove in the drawer skipped the removed garment (2026-10-22).
    function addTier(tier, button) {
      if (button.hasAttribute('aria-busy')) return;
      button.setAttribute('aria-busy', 'true');
      refresh()
        .catch(() => {})
        .then(() => {
          button.removeAttribute('aria-busy');
          addTierNow(tier, button);
        });
    }

    function addTierNow(tier, button) {
      const garments = [...tier.querySelectorAll('[data-fb-stage-garment]')].filter((g) => !g.classList.contains('is-added'));
      lastTier = tier;
      if (!garments.length) {
        const cart = document.querySelector('cart-drawer');
        if (cart && cart.open) cart.open();
        else window.location = window.routes.cart_url;
        return;
      }
      const missing = garments.find((g) => !chosen(g));
      if (missing) {
        open(missing, true, true);
        pending = tier;
        const msg = panelOf(missing).querySelector('[data-fb-msg]');
        if (msg) msg.textContent = S.pickSize;
        return;
      }
      const qty = new Map();
      garments.forEach((g) => {
        const id = chosen(g).id;
        qty.set(id, (qty.get(id) || 0) + 1);
      });
      const label = button.textContent;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      close(false);
      post([...qty].map(([id, quantity]) => ({ id, quantity })))
        .catch((err) => {
          console.error('[fb-bundle-stage]', err);
          const status = tier.querySelector('[data-fb-status]');
          if (status) status.textContent = (window.cartStrings && window.cartStrings.error) || String(err.message || err);
        })
        .finally(() => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          button.textContent = label;
          reprice(tier);
        });
    }

    // ---- wiring ----
    root.querySelectorAll('[data-fb-stage-garment]').forEach((g) => {
      g.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || pinned) return;
        clearTimeout(closeTimer);
        if (current === g) return;
        clearTimeout(openTimer);
        openTimer = setTimeout(() => open(g, false, false), OPEN_DELAY);
      });
      g.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        closeSoon();
      });
      g.addEventListener('click', (e) => {
        e.preventDefault();
        // a click by keyboard (detail 0) moves focus into the panel
        if (current === g && pinned) return;
        pinned = false;
        pending = null;
        open(g, true, e.detail === 0);
      });
    });

    root.querySelectorAll('[data-fb-quick]').forEach((panel) => {
      panel.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') clearTimeout(closeTimer);
      });
      panel.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') closeSoon();
      });
      panel.addEventListener('click', (e) => {
        const g = current;
        if (!g) return;
        pinned = true; // a click inside keeps it open
        if (e.target.closest('[data-fb-close]')) {
          close(true);
          return;
        }
        const sw = e.target.closest('[data-fb-v]');
        if (sw) {
          switchTo(g, sw.dataset.fbV).then(() => {
            const again = panelOf(g).querySelector(`[data-fb-v="${sw.dataset.fbV}"]`);
            if (again && document.activeElement === document.body) again.focus();
          });
          return;
        }
        const size = e.target.closest('[data-fb-size]');
        if (size) {
          g.dataset.variant = size.dataset.fbSize;
          const tier = g.closest('.fb-bundle__tier');
          if (pending === tier) {
            reprice(tier);
            addTier(tier, tier.querySelector('[data-fb-stage-add]'));
            return;
          }
          // the size click IS the add (owner, 2026-10-22)
          size.setAttribute('aria-pressed', 'true');
          reprice(tier);
          addOne(g, size);
          return;
        }
        const add = e.target.closest('[data-fb-quick-add]');
        if (add) addOne(g, add);
      });
    });

    root.querySelectorAll('[data-fb-stage-add]').forEach((button) => {
      button.addEventListener('click', () => addTier(button.closest('.fb-bundle__tier'), button));
    });

    document.addEventListener('click', (e) => {
      if (!current) return;
      if (e.target.closest('[data-fb-quick]') || e.target.closest('[data-fb-stage-garment]')) return;
      if (!e.target.isConnected) return; // the panel re-rendered under the click
      close(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && current) close(true);
    });

    root.querySelectorAll('.fb-bundle__tier').forEach(reprice);

    // Any cart change re-renders the header's cart count (Dawn's drawer on a remove or a quantity
    // change, the drawer's rail, the product form, this stage): watch it and re-read the cart.
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) new MutationObserver(refreshSoon).observe(bubble, { childList: true, subtree: true, characterData: true });
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refreshSoon);
    }
    refresh().catch(() => {});
  }

  const start = () => document.querySelectorAll('fb-bundle[data-fb-quick]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
